<template>
  <DialogBase
    :visible.sync="visible"
    title="File & Hình ảnh đính kèm"
    size="lg"
    :show-confirm="false"
    cancel-text="Đóng"
    :use-panel="true"
    panel-title="Danh sách tệp đính kèm"
    :panel-subtitle="`Đang có ${previewFiles.length} file/ảnh đính kèm trong nhận xét này`"
    panel-icon="el-icon-paperclip"
    @cancel="handleClose"
  >
    <div v-loading="loadingAll" class="evaluation-file-preview">
      <!-- 1. IMAGE SECTION (Hiển thị ảnh ra luôn, cho phép phóng to / tải về) -->
      <div v-if="imageFiles.length > 0" class="evl-preview-section">
        <div class="evl-preview-section__title">
          <i class="el-icon-picture" />
          <span>Hình ảnh đính kèm ({{ imageFiles.length }})</span>
          <span class="evl-preview-section__hint">· Nhấp vào ảnh để phóng to toàn màn hình</span>
        </div>

        <div class="evl-image-grid">
          <div
            v-for="(file, idx) in imageFiles"
            :key="file.objectKey || idx"
            class="evl-image-card"
          >
            <div class="evl-image-wrapper" @click="triggerImageZoom(idx)">
              <!-- Element UI Image with built-in Lightbox zoom & preview -->
              <el-image
                :ref="`imgPreview_${idx}`"
                :src="file.previewUrl || file.downloadUrl"
                :preview-src-list="allImageUrls"
                fit="cover"
                class="evl-image-display"
              >
                <div slot="placeholder" class="evl-image-slot evl-image-loading">
                  <i class="el-icon-loading" />
                  <span>Đang tải ảnh...</span>
                </div>
                <div slot="error" class="evl-image-slot evl-image-error">
                  <i class="el-icon-picture-outline" />
                  <span>Lỗi tải ảnh</span>
                </div>
              </el-image>

              <!-- Hover Overlay -->
              <div class="evl-image-overlay">
                <span class="evl-overlay-badge">
                  <i class="el-icon-zoom-in" /> Phóng to
                </span>
              </div>
            </div>

            <!-- Image Info & Action Buttons -->
            <div class="evl-image-info">
              <div class="evl-image-meta">
                <span class="evl-image-name" :title="file.rawName">
                  {{ file.displayName }}
                </span>
                <span class="evl-image-ext-badge">{{ file.extension.toUpperCase() }}</span>
              </div>
              <div class="evl-image-actions">
                <el-tooltip content="Phóng to ảnh" placement="top">
                  <el-button
                    type="primary"
                    size="mini"
                    circle
                    plain
                    icon="el-icon-zoom-in"
                    @click.stop="triggerImageZoom(idx)"
                  />
                </el-tooltip>
                <el-tooltip content="Tải ảnh về máy" placement="top">
                  <el-button
                    type="success"
                    size="mini"
                    circle
                    plain
                    icon="el-icon-download"
                    :loading="loadingKey === file.objectKey"
                    @click.stop="download(file)"
                  />
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. DOCUMENT / NON-IMAGE SECTION -->
      <div v-if="docFiles.length > 0" class="evl-preview-section">
        <div class="evl-preview-section__title">
          <i class="el-icon-document" />
          <span>Tài liệu đính kèm ({{ docFiles.length }})</span>
        </div>

        <div class="evl-doc-grid">
          <div
            v-for="file in docFiles"
            :key="file.objectKey"
            class="evl-doc-card"
          >
            <div :class="['evl-doc-icon', `evl-doc-icon--${file.fileType}`]">
              <i :class="getFileIcon(file)" />
            </div>

            <div class="evl-doc-details">
              <span class="evl-doc-name" :title="file.rawName">
                {{ file.displayName }}
              </span>
              <div class="evl-doc-sub-row">
                <span class="evl-doc-badge">{{ getFileTypeName(file) }}</span>
                <span v-if="file.extension" class="evl-doc-ext">.{{ file.extension }}</span>
              </div>
            </div>

            <div class="evl-doc-actions">
              <el-button
                v-if="file.isPdf || file.isOffice"
                type="primary"
                size="mini"
                plain
                icon="el-icon-view"
                :loading="loadingKey === file.objectKey"
                @click="preview(file)"
              >
                Xem
              </el-button>
              <el-button
                type="success"
                size="mini"
                plain
                icon="el-icon-download"
                :loading="loadingKey === file.objectKey"
                @click="download(file)"
              >
                Tải về
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. EMPTY STATE -->
      <div v-if="previewFiles.length === 0 && !loadingAll" class="evl-preview-empty">
        <div class="evl-empty-icon"><i class="el-icon-document" /></div>
        <p class="evl-empty-text">Không có file hoặc hình ảnh đính kèm nào.</p>
      </div>
    </div>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "EvaluationFilePreviewDialog",
  components: { DialogBase },
  data() {
    return {
      visible: false,
      loadingAll: false,
      loadingKey: "",
      previewFiles: [],
    };
  },
  computed: {
    imageFiles() {
      return this.previewFiles.filter((f) => f.isImage);
    },
    docFiles() {
      return this.previewFiles.filter((f) => !f.isImage);
    },
    allImageUrls() {
      return this.imageFiles
        .map((f) => f.previewUrl || f.downloadUrl)
        .filter(Boolean);
    },
  },
  methods: {
    async open(files) {
      this.visible = true;
      let imgCount = 0;
      let docCount = 0;

      this.previewFiles = (files || []).map((item) => {
        const objectKey =
          typeof item === "string"
            ? item
            : item.objectKey || item.url || item.name || "";
        const rawName = String(objectKey).split("/").pop() || "file";
        const extension = rawName.includes(".")
          ? rawName.split(".").pop().toLowerCase()
          : "";
        const isImage = [
          "jpg",
          "jpeg",
          "png",
          "gif",
          "webp",
          "bmp",
          "svg",
        ].includes(extension);
        const isPdf = extension === "pdf";
        const isOffice = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(
          extension
        );

        let fileType = "other";
        if (isImage) fileType = "image";
        else if (isPdf) fileType = "pdf";
        else if (["doc", "docx"].includes(extension)) fileType = "word";
        else if (["xls", "xlsx"].includes(extension)) fileType = "excel";

        // Generate clean, user-friendly display name
        let displayName = "";
        const prefixMatch = rawName.match(/^evaluation-\d+_(.+)$/i);
        if (prefixMatch && prefixMatch[1]) {
          displayName = prefixMatch[1].replace(/_/g, " ");
        } else {
          const isAutoGenerated =
            /^evaluation-\d+(\.[a-zA-Z0-9]+)?$/i.test(rawName) ||
            /^[a-f0-9-]{16,}(\.[a-zA-Z0-9]+)?$/i.test(rawName);

          if (isAutoGenerated) {
            if (isImage) {
              imgCount++;
              displayName = `Hình ảnh đính kèm ${imgCount}`;
            } else if (fileType === "excel") {
              docCount++;
              displayName = `Bảng tính Excel ${docCount}`;
            } else if (fileType === "word") {
              docCount++;
              displayName = `Tài liệu Word ${docCount}`;
            } else if (isPdf) {
              docCount++;
              displayName = `Tài liệu PDF ${docCount}`;
            } else {
              docCount++;
              displayName = `Tệp đính kèm ${docCount}`;
            }
          } else {
            displayName = rawName.replace(/_/g, " ");
          }
        }

        return {
          objectKey,
          rawName,
          displayName,
          extension,
          fileType,
          isImage,
          isPdf,
          isOffice,
          previewUrl:
            typeof item === "object" && item.previewUrl ? item.previewUrl : "",
          downloadUrl:
            typeof item === "object" && item.downloadUrl ? item.downloadUrl : "",
          officePreviewUrl: "",
        };
      });

      // Tự động tải trước URL cho tất cả file (đặc biệt là ảnh để hiển thị ra luôn)
      await this.loadAllFileUrls();
    },

    async loadAllFileUrls() {
      if (this.previewFiles.length === 0) return;
      this.loadingAll = true;
      try {
        await Promise.all(
          this.previewFiles.map(async (file) => {
            if (!file.previewUrl && file.objectKey) {
              await this.getUrls(file, false);
            }
          })
        );
      } finally {
        this.loadingAll = false;
      }
    },

    async getUrls(file, showNotify = true) {
      if (file.previewUrl && file.downloadUrl) return file;
      this.loadingKey = file.objectKey;
      try {
        const response = await EvaluationService.getEvaluationFileUrl(
          file.objectKey
        );
        const data = response.data.data || {};
        file.previewUrl = data.previewUrl || data.url || "";
        file.downloadUrl = data.downloadUrl || data.previewUrl || "";
        file.officePreviewUrl =
          file.isOffice && data.previewUrl
            ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                data.previewUrl
              )}`
            : "";
        return file;
      } catch (error) {
        if (showNotify) {
          NotifyMessage.leftCornerNotificationError(
            error?.response?.data?.message || "Không thể tải đường dẫn file!"
          );
        }
        return null;
      } finally {
        this.loadingKey = "";
      }
    },

    triggerImageZoom(idx) {
      const refName = `imgPreview_${idx}`;
      const imgComponent = this.$refs[refName];
      if (imgComponent) {
        const el = Array.isArray(imgComponent)
          ? imgComponent[0]?.$el
          : imgComponent?.$el;
        const imgTag = el?.querySelector("img");
        if (imgTag) {
          imgTag.click();
          return;
        }
      }
      const file = this.imageFiles[idx];
      if (file && file.previewUrl) {
        window.open(file.previewUrl, "_blank");
      }
    },

    async preview(file) {
      const previewWindow = window.open("about:blank", "_blank");
      const loadedFile = await this.getUrls(file);
      const previewUrl =
        loadedFile && (loadedFile.officePreviewUrl || loadedFile.previewUrl);
      if (previewUrl && previewWindow) {
        previewWindow.location.href = previewUrl;
      } else if (previewWindow) {
        previewWindow.close();
      }
    },

    async download(file) {
      const loadedFile = await this.getUrls(file);
      if (loadedFile && (loadedFile.downloadUrl || loadedFile.previewUrl)) {
        const url = loadedFile.downloadUrl || loadedFile.previewUrl;
        const downloadName =
          loadedFile.displayName && loadedFile.extension
            ? loadedFile.displayName.endsWith(`.${loadedFile.extension}`)
              ? loadedFile.displayName
              : `${loadedFile.displayName}.${loadedFile.extension}`
            : loadedFile.rawName || "tep-dinh-kem";

        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = downloadName;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          NotifyMessage.leftCornerNotificationSuccess("Tải file thành công!");
        } catch {
          const link = document.createElement("a");
          link.href = url;
          link.download = downloadName;
          link.target = "_blank";
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },

    getFileIcon(file) {
      if (file.isPdf) return "el-icon-document";
      if (file.fileType === "word") return "el-icon-document-copy";
      if (file.fileType === "excel") return "el-icon-s-grid";
      return "el-icon-document";
    },

    getFileTypeName(file) {
      if (file.isPdf) return "Tài liệu PDF";
      if (file.fileType === "word") return "Văn bản Word";
      if (file.fileType === "excel") return "Bảng tính Excel";
      return "Tệp tin";
    },

    handleClose() {
      this.visible = false;
      this.previewFiles = [];
      this.loadingKey = "";
      this.loadingAll = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.evaluation-file-preview {
  max-height: 70vh;
  overflow-y: auto;
  padding: 4px 2px;
}

.evl-preview-section {
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 8px;
  }
}

.evl-preview-section__title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  i {
    color: #2563eb;
    font-size: 16px;
  }
}

.evl-preview-section__hint {
  font-size: 12.5px;
  font-weight: 400;
  color: #64748b;
}

/* ─── Image Gallery Grid ────────────────────────── */
.evl-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
}

.evl-image-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.12);
    transform: translateY(-2px);
  }
}

.evl-image-wrapper {
  position: relative;
  width: 100%;
  height: 160px;
  background: #f8fafc;
  cursor: pointer;
  overflow: hidden;

  &:hover .evl-image-overlay {
    opacity: 1;
  }
}

.evl-image-display {
  width: 100%;
  height: 100%;
  display: block;

  /deep/ img {
    transition: transform 0.3s ease;
  }

  &:hover /deep/ img {
    transform: scale(1.04);
  }
}

.evl-image-slot {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #94a3b8;

  i {
    font-size: 24px;
  }
}

.evl-image-error {
  background: #fef2f2;
  color: #ef4444;
}

.evl-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(1.5px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.evl-overlay-badge {
  background: #ffffff;
  color: #1e293b;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.evl-image-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
}

.evl-image-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.evl-image-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.evl-image-ext-badge {
  font-size: 10.5px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
  flex-shrink: 0;
}

.evl-image-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* ─── Document Grid ─────────────────────────────── */
.evl-doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}

.evl-doc-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: all 0.15s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
}

.evl-doc-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;

  &--pdf {
    background: #fef2f2;
    color: #ef4444;
  }

  &--word {
    background: #eff6ff;
    color: #1d4ed8;
  }

  &--excel {
    background: #ecfdf5;
    color: #059669;
  }
}

.evl-doc-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.evl-doc-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.evl-doc-sub-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.evl-doc-badge {
  font-size: 11.5px;
  color: #64748b;
  white-space: nowrap;
}

.evl-doc-ext {
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 0 4px;
  border-radius: 3px;
  text-transform: uppercase;
}

.evl-doc-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* ─── Empty state ───────────────────────────────── */
.evl-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: #94a3b8;
}

.evl-empty-icon {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 10px;
}

.evl-empty-text {
  font-size: 13.5px;
  margin: 0;
}
</style>
